import Gettext from 'gettext.js';
import { sprintf } from 'i18n/format';
import { setActiveLocale } from 'i18n/locale';

// gettext.js's built-in strfmt uses %1/%2 placeholders, but the shared .po
// catalog (PHP + JS) uses printf-style ones. We do our own sprintf-style
// substitution (i18n/format) after the gettext lookup so JS and PHP can share
// msgids.

// gettext.js runs every lookup result through its own strfmt, which turns %%
// into % and %1 into its first extra argument (undefined here). Doubling every
// % in the catalog and in the msgids we look up makes that pass a no-op, so
// sprintf above sees the catalog text unchanged.
const escapePercent = (text) => String(text).replace(/%/g, '%%');

const escapeCatalog = (messages) => {
	const escaped = {};
	for (const [key, value] of Object.entries(messages)) {
		if (key === '') {
			escaped[key] = value;
		} else {
			escaped[escapePercent(key)] = Array.isArray(value) ? value.map(escapePercent) : escapePercent(value);
		}
	}
	return escaped;
};

let active = {
	__: (msgid, ...args) => sprintf(msgid, args),
	__n: (singular, plural, count, ...args) => sprintf(count === 1 ? singular : plural, args),
	__x: (_context, msgid, ...args) => sprintf(msgid, args),
	instance: null,
};

const createI18n = ({ locale, messages, domain = 'messages', attachGlobals = false } = {}) => {
	const instance = new Gettext({ locale });

	if (messages) {
		instance.loadJSON(escapeCatalog(messages), domain);
	}

	const __ = (msgid, ...args) => sprintf(instance.gettext(escapePercent(msgid)), args);
	const __n = (singular, plural, count, ...args) =>
		sprintf(instance.ngettext(escapePercent(singular), escapePercent(plural), count), args);
	const __x = (context, msgid, ...args) =>
		sprintf(instance.pgettext(escapePercent(context), escapePercent(msgid)), args);

	const api = { __, __n, __x, instance, locale };

	if (attachGlobals && typeof window !== 'undefined') {
		window.__ = __;
		window.__n = __n;
		window.__x = __x;
	}

	active = api;
	setActiveLocale(locale);

	return api;
};

// Makes an instance made earlier the active translator again (the ssr service
// keeps one per locale and switches between requests).
const activateI18n = (api) => {
	active = api;
	setActiveLocale(api.locale);
	return api;
};

const bootstrap = () => {
	if (typeof window === 'undefined') return null;

	const data = window.__locale_data;
	if (!data) return null;

	return createI18n({
		locale: data.locale,
		messages: data.messages,
		domain: data.domain || 'messages',
		attachGlobals: true,
	});
};

const __ = (msgid, ...args) => active.__(msgid, ...args);
const __n = (singular, plural, count, ...args) => active.__n(singular, plural, count, ...args);
const __x = (context, msgid, ...args) => active.__x(context, msgid, ...args);

// Auto-bootstrap from window.__locale_data (set by the locale/<locale>/messages.<hash>.js
// script the layout loads before the bundle) when present (browser only).
// Returns null and leaves the passthrough translator in place in non-browser / test contexts.
bootstrap();

export { createI18n, activateI18n, bootstrap, __, __n, __x };
export default createI18n;
